import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, Loader2, Copy, ExternalLink, CheckCircle2, KeyRound, MonitorPlay } from "lucide-react";

// Admin queue for captcha-walled ATS account setup (Dayforce). Each row shows the candidate, the managed
// login to create the account with, and the LIVE verification code once the ATS emails it to our managed
// inbox — so the admin creates the account without needing inbox access. "Mark created" verifies the
// candidate's global login and auto-re-queues their waiting applications.

const copy = (v, label) => { navigator.clipboard?.writeText(v || ""); toast.success(`${label} copied`); };

function Field({ label, value, mono }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-muted-foreground mb-0.5">{label}</div>
      <div className="flex items-center gap-1.5">
        <span className={`truncate text-sm ${mono ? "font-mono" : ""} text-[#0F172A]`}>{value || "—"}</span>
        {value && (
          <button onClick={() => copy(value, label)} className="text-muted-foreground hover:text-[#0025D8] flex-shrink-0" title={`Copy ${label}`}>
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminAtsSetups() {
  const [setups, setSetups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/ats-setups")
      .then(({ data }) => setSetups(data.setups || []))
      .catch(() => toast.error("Could not load account-setup queue"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);   // auto-refresh so the verification code surfaces as it arrives
    return () => clearInterval(t);
  }, [load]);

  const openSession = async (task) => {
    setBusyId(task.task_id);
    try {
      const { data } = await api.post(`/admin/ats-setups/${task.task_id}/open-session`);
      // Don't window.open here — browsers block popups fired after an await. Show the link to click.
      if (data.viewer_url) toast.success("Live browser ready — click 'Open live browser' below to sign in.");
      load();   // refreshes the card so the viewer link (steel_viewer_url) appears
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open browser session");
    } finally { setBusyId(null); }
  };

  const captureSession = async (task) => {
    setBusyId(task.task_id);
    try {
      const { data } = await api.post(`/admin/ats-setups/${task.task_id}/capture-session`);
      toast.success(`Signed in — session saved. ${data.requeued || 0} application(s) re-queued.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Not signed in yet — finish sign-in in the browser, then try again.");
    } finally { setBusyId(null); }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 py-8">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#0F172A] mb-4">
        <ArrowLeft className="w-4 h-4" /> Admin
      </Link>
      <div className="flex items-center justify-between gap-3 mb-1">
        <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2">
          <KeyRound className="w-6 h-6 text-[#0025D8]" /> Account setup queue
        </h1>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#0025D8]">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>
      <div className="text-sm text-muted-foreground mb-6 space-y-2">
        <p>
          Dayforce sign-up <i>and</i> login are reCAPTCHA-gated, so they must be done in a real browser by a
          human. Fastest way — <b>your own browser</b>:
        </p>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Sign in to <b>jobs.dayforcehcm.com</b> (create the account first if new — login + code shown below).</li>
          <li>Open the <b>VetraHub Apply</b> extension → <b>Load candidates</b> → pick this person → <b>Capture session</b>.</li>
        </ol>
        <p>We vault the signed-in session and the waiting applications submit automatically. No slow cloud browser
          needed — the “Open browser to sign in” option below is only a fallback.</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : setups.length === 0 ? (
        <div className="rounded-2xl border border-border bg-white p-10 text-center text-muted-foreground">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-[#10B981]" />
          Nothing to set up right now.
        </div>
      ) : (
        <div className="space-y-4">
          {setups.map((t) => (
            <div key={t.task_id} className="rounded-2xl border border-border bg-white p-5">
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <div>
                    <div className="font-semibold text-[#0F172A]">{t.candidate_name}</div>
                    <div className="text-xs text-muted-foreground">{t.candidate_email} · {t.pending_jobs} application{t.pending_jobs !== 1 ? "s" : ""} waiting</div>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${t.kind === "signin" ? "bg-[#0025D8]/10 text-[#0025D8]" : "bg-[#7C3AED]/10 text-[#7C3AED]"}`}>
                    {t.kind === "signin" ? "Sign in (account exists)" : "Create account"}
                  </span>
                </div>
              </div>
              {t.kind !== "signin" && (t.first_name || t.last_name) && (
                <div className="grid sm:grid-cols-2 gap-4 rounded-xl bg-[#7C3AED]/5 border border-[#7C3AED]/20 p-3.5 mb-3">
                  <Field label="First name (for the form)" value={t.first_name} />
                  <Field label="Last name (for the form)" value={t.last_name} />
                </div>
              )}
              <div className="grid sm:grid-cols-3 gap-4 rounded-xl bg-secondary/40 border border-border p-3.5 mb-4">
                <Field label="Login email" value={t.login_email} mono />
                <Field label="Password" value={t.login_password} mono />
                <div className="min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-muted-foreground mb-0.5">Verification code</div>
                  {t.verification_code ? (
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-lg font-bold text-[#047857] tracking-widest">{t.verification_code}</span>
                      <button onClick={() => copy(t.verification_code, "Code")} className="text-muted-foreground hover:text-[#0025D8]" title="Copy code"><Copy className="w-3.5 h-3.5" /></button>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> waiting for email…</span>
                  )}
                </div>
              </div>
              {t.steel_viewer_url && (
                <div className="mb-3 flex items-center gap-2 rounded-lg border border-[#0025D8]/25 bg-[#0025D8]/5 px-3 py-2">
                  <MonitorPlay className="w-4 h-4 text-[#0025D8] flex-shrink-0" />
                  <span className="text-[13px] text-slate-700">Live browser is ready.</span>
                  <a href={t.steel_viewer_url} target="_blank" rel="noreferrer"
                    className="text-[13px] font-semibold text-[#0025D8] hover:underline inline-flex items-center gap-1">
                    Open live browser <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  <span className="text-[11px] text-muted-foreground ml-auto">sign in there, then click "I've signed in" · ~14 min</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-xs text-muted-foreground max-w-[46ch]">
                  {t.kind === "signin"
                    ? "This account already exists — just sign in (Dayforce login is captcha-gated, so it must be done in-browser). We'll capture the session and the applications submit automatically."
                    : "Create the account with the login above (the code appears here), then finish — we capture the signed-in session automatically."}
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => openSession(t)} disabled={busyId === t.task_id}
                    className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-lg border border-[#0025D8]/30 text-[#0025D8] hover:bg-[#0025D8]/5 disabled:opacity-50 transition-colors">
                    <MonitorPlay className="w-4 h-4" /> {t.steel_viewer_url ? "New browser" : "Open browser to sign in"}
                  </button>
                  <button onClick={() => captureSession(t)} disabled={busyId === t.task_id}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-[#10B981] text-white hover:bg-[#059669] disabled:opacity-50 transition-colors">
                    {busyId === t.task_id ? <><Loader2 className="w-4 h-4 animate-spin" /> …</> : <><CheckCircle2 className="w-4 h-4" /> I've signed in — finish</>}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
